import React from "react";
import { ChatProvider, useChat } from "../../context/ChatContext";
import { createChatClient } from "../../lib/chatApi";
import type { Product } from "../../lib/mockData";
import { ProductCandidatesGrid } from "../../components/ProductCandidatesGrid";
import { ProductPanel } from "../../components/ProductPanel";
import { mapRecommendedToProduct } from "../../lib/utils";
import { SearchInput } from "../../components/ChatInput";
import { ProductCardGroup } from "../../components/ProductCardGroup";
import { RotateCcw, Home, Check } from "lucide-react";
import { OptionCardCarousel } from "../components/OptionCardCarousel";
import {
  appendTestSessionId,
  getBackendBaseUrl,
  persistTestConfig,
  persistTestSessionId,
  updateGlobalTestConfig,
} from "../config";
import { useLocale } from "../../context/LocaleContext";

interface BrowserWidgetProps {
  storeId: string;
  apiBase: string;
  initialQuery?: string;
  onRetry?: () => void;
  onClose?: () => void;
}

export function BrowserWidget({ storeId, apiBase, initialQuery, onRetry, onClose }: BrowserWidgetProps) {
  // Mirror SPA dev/prod selection if present (defaults to prod)
  const getBackendMode = React.useCallback((): 'dev' | 'prod' => {
    try {
      const q = new URL(window.location.href).searchParams.get('backend');
      if (q === 'dev' || q === 'prod') return q;
    } catch {}
    try {
      const saved = localStorage.getItem('ww/spa/backendMode');
      if (saved === 'dev' || saved === 'prod') return saved as 'dev' | 'prod';
    } catch {}
    return 'prod';
  }, []);

  const getOrigins = React.useCallback((mode: 'dev' | 'prod') => {
    const PROD = getBackendBaseUrl(); // Use configurable backend URL
    const DEV = 'https://wandway-back.onrender.com'; // Dev mode still uses hardcoded URL
    const origin = (mode === 'prod' ? PROD : DEV).replace(/\/$/, '');
    return {
      initUrl: `${origin}/widget/init`,
      chatBase: `${origin}/chat`,
    };
  }, []);

  const refreshToken = React.useCallback(async () => {
    try {
      const MODE = getBackendMode();
      const { initUrl, chatBase } = getOrigins(MODE);
      const url = `${initUrl}?store_id=${encodeURIComponent(storeId)}`;
      const urlWithSession = appendTestSessionId(url);
      const res = await fetch(urlWithSession, { 
        method: "GET", 
        credentials: "omit", 
        cache: "no-store",
        mode: "cors"
      });
      if (!res.ok) return null;
      const data = await res.json();
      persistTestSessionId(data.test_session_id);
      persistTestConfig(data.test_config);
      updateGlobalTestConfig({ testSessionId: data.test_session_id, testConfig: data.test_config });
      const base = (data.api_base || "").replace(/\/$/, "");
      const secureBase = base.replace(/^http:\/\//i, "https://");
      const fixedApiBase = /\/chat$/.test(secureBase) ? secureBase : `${secureBase}/chat`;
      const nextToken = data.token as string | undefined;
      if (!nextToken) return null;
      const nextBase = (MODE === 'prod') ? fixedApiBase : chatBase;
      const g = (window as any).__WW__ || {};
      (window as any).__WW__ = {
        ...g,
        storeId,
        apiBase: nextBase,
        themeCssUrl: g.themeCssUrl,
        flags: data.flags,
        ...(data.test_session_id ? { testSessionId: data.test_session_id } : {}),
        ...(Array.isArray(data.test_config) ? { testConfig: data.test_config } : {}),
      };
      return { token: nextToken, apiBase: nextBase };
    } catch {
      return null;
    }
  }, [storeId, getBackendMode, getOrigins]);

  const client = React.useMemo(() => createChatClient({ storeId, apiBase, refreshToken }), [storeId, apiBase, refreshToken]);

  return (
    <ChatProvider client={client}>
      <BrowserWidgetInner initialQuery={initialQuery} onRetry={onRetry} onClose={onClose} />
    </ChatProvider>
  );
}

function BrowserWidgetInner({ initialQuery, onRetry, onClose }: { initialQuery?: string; onRetry?: () => void; onClose?: () => void }) {
  const { t } = useLocale();
  const { resetChat, clearTurn, sendMessage, messages, loading, setCurrentProduct, selectOption, resetShoppingList, candidatesPagination, fetchMoreCandidates, onProductClick } = useChat();
  const anyChat: any = useChat();
  const browserMeta = (anyChat as any).browser as { queryTitle?: string; resultsCount?: number; phase?: 'recommendation' | 'question'; resultsDescription?: string } | null;
  const status = (anyChat as any).status as { title?: string; status?: string } | null;
  const staticPrefix = (anyChat as any).staticPrefix as string | null;
  const forceShowInput = Boolean((anyChat as any).forceShowInput);
  const isPrompt = staticPrefix === "prompt.prefix_answer";
  const performGuidedAction = (anyChat as any).performGuidedAction as ((a: any) => void) | undefined;
  const [selectedProduct, setSelectedProduct] = React.useState<Product | null>(null);
  const closeBtnRef = React.useRef<HTMLButtonElement>(null);
  const previousFocusRef = React.useRef<HTMLElement | null>(null);

  // Mirror Chat loader UX: show fallback "Tänker..." after 2s when loading
  const [showThinking, setShowThinking] = React.useState(false);
  React.useEffect(() => {
    let timer: number;
    if (loading) {
      timer = window.setTimeout(() => setShowThinking(true), 2000);
    } else {
      setShowThinking(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [loading]);

  // On mount: start a new session with the initial query (if provided)
  React.useEffect(() => {
    const q = (initialQuery || '').trim();
    if (!q) return;
    resetChat();
    sendMessage(q, true);
    // Do not mutate URL here; embed uses attributes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Latest assistant message that isn't a candidates packet (may be streaming or final)
  const lastAssistant = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (!m.isUser && m.type !== 'candidates') return m;
    }
    return null;
  }, [messages]);

  const lastCandidates = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (!m.isUser && m.type === 'candidates') return m;
    }
    return null;
  }, [messages]);

  // All assistant messages in order (excluding candidates which are handled separately)
  const assistantMessages = React.useMemo(() => {
    return messages.filter((m) => !m.isUser && m.type !== 'candidates');
  }, [messages]);

  // Identify the current prompt message to render as the card (so it stays visible even after explanation)
  const promptCardId = React.useMemo(() => {
    if (!isPrompt) return null;
    // Find the most recent prompt message
    for (let i = assistantMessages.length - 1; i >= 0; i--) {
      const mm = assistantMessages[i] as any;
      if (mm?.type === 'prompt') return mm.id;
    }
    return null;
  }, [isPrompt, assistantMessages]);

  // Identify the latest regular options question (non-ordered) and pin it as a card
  const optionsCardId = React.useMemo(() => {
    for (let i = assistantMessages.length - 1; i >= 0; i--) {
      const mm = assistantMessages[i] as any;
      if (Array.isArray(mm?.options) && mm.options.length > 0 && mm?.type !== 'ordered_options') {
        return mm.id as string;
      }
    }
    return null;
  }, [assistantMessages]);

  // Determine which message should be shown as a card: prompt takes priority, then result
  const cardMessageId = React.useMemo(() => {
    if (promptCardId) return promptCardId;
    if (optionsCardId) return optionsCardId;
    return (lastAssistant as any)?.type === 'result' ? (lastAssistant as any).id : null;
  }, [promptCardId, optionsCardId, lastAssistant]);

  // Simplified button-hide state: one explanation per turn (resets when new prompt appears)
  const [hideExplain, setHideExplain] = React.useState(false);
  React.useEffect(() => {
    // Reset button visibility when a new prompt appears
    setHideExplain(false);
  }, [cardMessageId]);

  const products = React.useMemo<Product[]>(() => {
    const recs: any[] = Array.isArray((lastCandidates as any)?.candidates) ? (lastCandidates as any).candidates : [];
    const sid = (lastCandidates as any)?.search_id as string | undefined;
    return recs.map((r) => mapRecommendedToProduct(r, sid));
  }, [lastCandidates]);

  const hasProducts = products.length > 0;
  const resultsDescription = browserMeta?.resultsDescription;
  
  // Final recommended products (from terminal result payload)
  const finalProducts = React.useMemo<Product[]>(() => {
    if (!lastAssistant || (lastAssistant as any)?.type !== 'result') return [];
    const recs: any[] = (lastAssistant as any)?.recommendation?.recommended_products ?? [];
    const sid = (lastAssistant as any)?.search_id as string | undefined;
    return Array.isArray(recs) ? recs.map((r: any) => mapRecommendedToProduct(r, sid)) : [];
  }, [lastAssistant]);
  const lastErrorText = React.useMemo(() => {
    return lastAssistant && (lastAssistant as any)?.type === 'error'
      ? (lastAssistant.text || t("common.error_generic"))
      : null;
  }, [lastAssistant]);

  // Input should only appear when the flow expects free-text (prompt/answer) or explicitly when answering a prompt
  const showInput = React.useMemo(() => {
    if (forceShowInput) return true;
    if (staticPrefix === "prompt.prefix_answer") return true;
    const t = (lastAssistant as any)?.type;
    if (t === 'prompt' || t === 'answer') return true;
    return Boolean(optionsCardId);
  }, [forceShowInput, staticPrefix, lastAssistant, optionsCardId]);

  const handleProductSelect = (p: Product) => {
    onProductClick(); // Track product click
    setSelectedProduct(p);
    setCurrentProduct(p.id);
  };

  const handleClosePanel = () => {
    setSelectedProduct(null);
    setCurrentProduct(null);
  };

  // Focus management for panel open/close (match Chat behavior)
  React.useEffect(() => {
    if (selectedProduct) {
      previousFocusRef.current = (document.activeElement as HTMLElement) || null;
      setTimeout(() => closeBtnRef.current?.focus(), 0);
    } else {
      previousFocusRef.current?.focus?.();
      previousFocusRef.current = null;
    }
  }, [selectedProduct]);

  // Allow Esc to close while the panel is open
  React.useEffect(() => {
    if (!selectedProduct) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClosePanel();
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true } as any);
  }, [selectedProduct]);

  return (
    <div className="ww-browser-widget h-full w-full flex flex-col relative">
      {/* Top bar: icon buttons on both sides (retry / close) */}
      <div className="flex items-center justify-between p-[12px]">
        <button
          type="button"
          className="ww-icon-btn w-[36px] h-[36px] flex items-center justify-center rounded-full border"
          aria-label={t("common.retry")}
          onClick={onRetry}
          title={t("common.retry")}
        >
          <RotateCcw className="w-[20px] h-[20px]" />
        </button>
        {onClose ? (
          <button
            type="button"
            className="ww-icon-btn w-[36px] h-[36px] flex items-center justify-center rounded-full border"
            aria-label={t("common.close")}
            onClick={onClose}
            title={t("common.close")}
          >
            <Home className="w-[24px] h-[24px]" />
          </button>
        ) : (
          <div className="w-[36px]" />
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1000px] mx-auto">
          <div className="flex flex-col gap-1 px-4">
            <h2 className="text-[52px] text-left font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
              {browserMeta?.queryTitle || t("common.results")}
            </h2>
            {typeof browserMeta?.resultsCount === 'number' && (
              <span className="text-[14px] text-left" style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-body)' }}>
                {browserMeta.resultsCount} {t("common.hits")}
              </span>
            )}
          </div>
          {lastErrorText && (
            <div className="px-4 pt-3">
              <div className="ww-alert ww-alert-error" role="alert">
                {lastErrorText}
              </div>
            </div>
          )}
          <section className="px-4">
            <div className="mb-4 space-y-3">
              {hasProducts && (
                <div className="text-left">
                  <span className="text-[14px]" style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-body)' }}>
                    {t("browser.refine_hint")}{' '}
                    <button
                      type="button"
                      onClick={onRetry}
                      className="underline hover:no-underline cursor-pointer"
                      style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-body)' }}
                    >
                      {t("common.retry")}
                    </button>
                  </span>
                </div>
              )}
              <div
                className="text-left text-[14px] min-h-[20px]"
                aria-live="polite"
                style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-body)' }}
              >
                {resultsDescription || ''}
              </div>
            </div>
            <ProductCandidatesGrid
              products={products}
              onProductSelect={handleProductSelect}
              hasMore={candidatesPagination[lastCandidates?.id || '']?.hasMore || false}
              isLoadingMore={candidatesPagination[lastCandidates?.id || '']?.isLoadingMore || false}
              totalCount={candidatesPagination[lastCandidates?.id || '']?.totalCount}
              onLoadMore={lastCandidates?.id ? () => fetchMoreCandidates(lastCandidates.id) : undefined}
            />
          </section>

        {/* Assistant area: streamed text and final content below the grid, in-flow */}
        <section className="p-4 mt-[40px] mb-[40px] border-t">
          <div className="space-y-3">
            {/* Render all assistant messages in order; prompt card stays visible, explanations append below */}
            {assistantMessages.map((m) => {
              const showCard = m.id === cardMessageId;
              return (
                <div key={m.id}>
                  {m.text && (
                    showCard ? (
                      <div className="text-left items-start border rounded-xl p-[16px]" style={{ borderColor: 'var(--secondary)' }}>
                        <div className="text-[16px]" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>
                          {m.text}
                        </div>
                        {performGuidedAction && !hideExplain && ((m.id === promptCardId && isPrompt) || (m.id === optionsCardId)) && (
                          <button
                            type="button"
                            className="mt-2 underline text-left"
                            onClick={() => {
                              setHideExplain(true);
                              performGuidedAction({
                                id: crypto.randomUUID?.() ?? String(Date.now()),
                                label: t("browser.explain_question"),
                                mode: "send",
                                route: "QUESTION",
                                template: "PROMPT_EXPLAIN",
                              } as any);
                            }}
                            style={{ color: 'var(--text-tertiary)', fontFamily: 'var(--font-body)' }}
                          >
                            {t("browser.explain_question")}
                          </button>
                        )}
                      </div>
                    ) : (m.type === 'ordered_options') ? (
                      <div className="text-left items-start border rounded-xl p-4" style={{ borderColor: 'var(--secondary)' }}>
                        <div className="text-[16px]" style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>
                          {m.text}
                        </div>
                      </div>
                    ) : (
                      <p className="text-[16px] text-left items-start" style={{ fontFamily: 'var(--font-body)' }}>{m.text}</p>
                    )
                  )}
                </div>
              );
            })}

            {/* Final: result cards */}
            {(lastAssistant as any)?.type === 'result' && finalProducts.length > 0 && (
              <ProductCardGroup products={finalProducts} onProductSelect={handleProductSelect} />
            )}

            {/* Final: options (single-pick) */}
            {(() => {
              const optionsCard = optionsCardId
                ? (assistantMessages.find((x) => (x as any).id === optionsCardId) as any)
                : null;
              return optionsCard && optionsCard?.type !== 'ordered_options' && Array.isArray(optionsCard?.options) && optionsCard.options.length > 0 ? (
                <FollowUpOptions
                  options={optionsCard.options}
                  onSelect={(opts) => { if (opts.length === 0) return; clearTurn(); selectOption(opts); }}
                />
              ) : null;
            })()}

            {/* Final: ordered options (Browser: single-pick card carousel) */}
            {(lastAssistant as any)?.type === 'ordered_options' && Array.isArray((lastAssistant as any)?.options) && (lastAssistant as any).options.length > 0 && (
              <OptionCardCarousel
                options={(lastAssistant as any).options as Array<{ index: number; label: string; image?: string; category?: string }>}
                onSelect={(opt) => {
                  // Single-pick: submit immediately and start a new turn
                  clearTurn();
                  selectOption([{ index: opt.index, label: opt.label }]);
                  // Clear any residual multi-pick state if present
                  try { (resetShoppingList as any)?.(); } catch {}
                }}
              />
            )}
          </div>
          
          {loading && (
            <div className="flex justify-center px-8 py-2">
              <div className="px-4 py-2 rounded-3xl inline-flex items-center">
                <span className="dot-spinner" />
                <div
                  key={`${status?.title ?? ''}::${status?.status ?? ''}`}
                  className="ml-[12px] text-left animate-in fade-in slide-in-from-top-4"
                  aria-live="polite"
                >
                  <span className="block text-[12px] font-semibold text-gray-700">
                    {status?.title || (showThinking ? t("common.thinking") : null)}
                  </span>
                  {status?.status && (
                    <span className="block text-[12px] text-gray-500 mt-0.5">{status.status}</span>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Input shown only for prompt/answer flows or explicit answer prefix */}
          {showInput && (
            <div className="mt-[40px]">
              <SearchInput
                placeholder={t("browser.input_placeholder")}
                selectedOptions={[]}
                onRemoveOption={() => {}}
                onSubmit={(val) => { const v = val.trim(); if (!v) return; clearTurn(); sendMessage(v); }}
                disabled={loading}
                staticPrefix={isPrompt ? undefined : (staticPrefix ? t(staticPrefix) : undefined)}
              />
            </div>
          )}
        </section>
        </div>
      </div>

      {/* Hidden ProductPanel portal (wired in later phases) */}
      <div id="ww-product-panel-portal" className="hidden" aria-hidden="true" />

      {/* Slide-over ProductPanel */}
      {selectedProduct && (
        <div
          className="hidden md:block absolute inset-0 z-40 backdrop-blur-sm bg-black/5 transition-opacity duration-300"
          onClick={handleClosePanel}
        />
      )}
      <div
        role="dialog"
        aria-modal="true"
        className={
          "absolute top-0 right-0 h-full z-50 transform-gpu transform transition-transform transition-opacity duration-300 ease-in-out bg-white" +
          "w-full md:w-1/2 " +
          (selectedProduct ? "translate-x-0 opacity-100 pointer-events-auto" : "translate-x-full opacity-0 pointer-events-none")
        }
      >
        <div className="h-full w-full relative" onClick={(e) => e.stopPropagation()}>
          <button
            className="ww-close-btn absolute top-[16px] left-[16px] z-10 w-[32px] h-[32px] flex items-center justify-center rounded-full"
            onClick={handleClosePanel}
            ref={closeBtnRef}
            aria-label="Close panel"
          >
            <svg className="w-[16px] h-[16px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {selectedProduct && <ProductPanel {...selectedProduct} />}
        </div>
      </div>
    </div>
  );
}

function FollowUpOptions({ options, onSelect }: { options: Array<{ index: number; label: string }>; onSelect: (opts: Array<{ index: number; label: string }>) => void }) {
  const [selectedIndices, setSelectedIndices] = React.useState<number[]>([]);
  const [hasConfirmed, setHasConfirmed] = React.useState(false);
  const toggle = (index: number) => {
    if (hasConfirmed) return;
    setSelectedIndices((prev) => prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]);
  };
  const handleConfirm = () => {
    if (selectedIndices.length === 0 || hasConfirmed) return;
    setHasConfirmed(true);
    const selected = options.filter((opt) => selectedIndices.includes(opt.index));
    if (selected.length === 0) return;
    onSelect(selected);
  };
  return (
    <div className="mt-[8px]">
      <div className="flex flex-wrap gap-[8px] items-center">
        {options.map((opt) => {
          const isSelected = selectedIndices.includes(opt.index);
          return (
            <button
              key={opt.index}
              type="button"
              onClick={() => toggle(opt.index)}
              disabled={hasConfirmed}
              aria-pressed={isSelected}
              className="ww-btn ww-btn-third"
              style={{
                borderColor: isSelected ? 'var(--primary)' : undefined,
                opacity: hasConfirmed && !isSelected ? 0.5 : 1,
              }}
            >
              {opt.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={selectedIndices.length === 0 || hasConfirmed}
          aria-label="Bekräfta val"
          className="w-10 h-10 rounded-full flex items-center justify-center ww-btn-primary"
          style={{
            opacity: selectedIndices.length === 0 || hasConfirmed ? 0.5 : 1,
            cursor: selectedIndices.length === 0 || hasConfirmed ? 'not-allowed' : 'pointer',
          }}
        >
          <Check className="w-[20px] h-[20px]" style={{ color: 'inherit' }} />
        </button>
      </div>
    </div>
  );
}

